import { Injectable } from '@angular/core';
import { Selector } from '@ngxs/store';
import { IPlanState, PlansState } from '@nibtravel/shared/data-access/plans';
import { IQuoteInput, QuoteState } from '@nibtravel/shared/data-access/quote';
import {
  IUnderwriterState,
  UnderwriterState,
} from '@nibtravel/shared/data-access/underwriter';
import {
  IDonation,
  IFootprintsAnalytics,
  IFootprintsDonationInput,
  IFundableProject,
} from '@nibtravel/shared/util/models';

import { FootprintsState } from '.';
import { IFootprintsState } from '../model';

@Injectable({ providedIn: 'root' })
export class FootprintsSelectors {
  @Selector([QuoteState.planId, PlansState])
  static currency(planId: number | undefined, plans: IPlanState) {
    return planId ? plans.planSummaries?.[planId].currencyCode || 'AUD' : 'AUD';
  }

  @Selector([FootprintsState])
  static getDonations(state: IFootprintsState): IDonation[] | undefined {
    return state.donations;
  }

  @Selector([FootprintsState, QuoteState.getFundableProjectId])
  static getProjects(state: IFootprintsState): IFundableProject[] {
    return state.fundableProjects;
  }

  @Selector([QuoteState.donationAmount])
  static donationAmount(donationAmount: number | undefined): number {
    return donationAmount ?? 0;
  }

  @Selector([UnderwriterState])
  static donationCode(underwriter: IUnderwriterState): string {
    const contractId = underwriter.currentContractId;
    return contractId
      ? underwriter.contractsMap[contractId].donationAccountCode
      : '';
  }

  @Selector([QuoteState.quoteInput, FootprintsSelectors.getProjects])
  static footprintsAnalytics(
    quote: IQuoteInput,
    projects: IFundableProject[]
  ): IFootprintsAnalytics | undefined {
    const donation = quote.footprintsDonation;
    if (
      donation?.donationAmount &&
      donation.donationAmount > 0 &&
      donation.donationProjectId
    ) {
      const project = projects.find(
        (p) => p.projectId === donation.donationProjectId
      );
      return project ? { project, donation } : undefined;
    }

    return undefined;
  }

  @Selector([QuoteState.quoteInput])
  static donationInput(
    quote: IQuoteInput
  ): IFootprintsDonationInput | undefined {
    return quote.footprintsDonation;
  }

  @Selector([FootprintsSelectors.getProjects])
  static selectedProject(projects: IFundableProject[]) {
    return projects.find((p) => p.isSelected);
  }

  @Selector([FootprintsSelectors.getDonations])
  static selectedAmount(donationBands: IDonation[]) {
    return donationBands.find((d) => d.isSelected);
  }
}
